import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import LazyLoad from 'react-lazy-load';
import mediaQueries from 'app/utils/mediaQueries';
import { decodeHtml } from 'app/utils/helpers';
import ProductItem from 'app/utils/productItem/productItem';
import dlTrackCrossSellProducts, { dlSelectCrossSellItem } from 'app/analytics/crossSellAnalytics';
import { dlTrackProductClick } from 'app/analytics/productListAnalytics';
import { LOAD_2_PRODUCTS_PER_ROW, LOAD_4_PRODUCTS_PER_ROW, DIGITS_AFTER_DOT } from './crossSellConstants';
// TODO: only product-carousel__title is consumed. Refactor ex: extract title into a separate typography style.
import '../../cmscomponents/carousel/productCarousel/product-carousel.scss';

const { locale } = window.inlineGlobalConfig;

const getProductAdapter = (product, metaInfo) => ({
  id: product.id,
  url: `/${locale.country}/${locale.language}${product.url}`,
  name: (metaInfo.displayProductTitles && product.name && decodeHtml(product.name.replace(/&amp;/g, '&'))) || '',
  description:
    (metaInfo.displayProductTitles && product.description && decodeHtml(product.description.replace(/&amp;/g, '&'))) ||
    '',
  altImage: product.imageSet[1],
  heroImage: product.imageSet[0],
  markdown: metaInfo.displayProductPrices && product.wasPrice && product.wasPrice.value - product.price.value > 0,
  price:
    (metaInfo.displayProductPrices &&
      product.price &&
      `${product.price.symbol}${Number.parseFloat(product.price.value).toFixed(DIGITS_AFTER_DOT)}`) ||
    '',
  wasPrice:
    (metaInfo.displayProductPrices &&
      product.wasPrice &&
      `${product.wasPrice.symbol}${Number.parseFloat(product.wasPrice.value).toFixed(DIGITS_AFTER_DOT)}`) ||
    '',
  primaryCategoryName: product.primaryCategoryName,
  productAge: product.productAge,
  heroBadges: product.heroBadges,
  promoBadges: product.promoBadges,
  dataLayerDescription: product.dataLayerDescription,
  classificationData: (() => {
    const { classificationData } = product;
    const entry = [];

    Object.keys(classificationData).forEach(key => {
      if (Object.prototype.hasOwnProperty.call(classificationData, key)) {
        entry.push({
          key,
          value: classificationData[key],
        });
      }
    });

    return { entry };
  })(),
});

class CrossSell extends Component {
  static productsPerRowForCurrentSize() {
    return mediaQueries.is_small_only() ? LOAD_2_PRODUCTS_PER_ROW : LOAD_4_PRODUCTS_PER_ROW;
  }

  constructor(props) {
    super(props);

    this.state = {
      productsPerRow: CrossSell.productsPerRowForCurrentSize(),
      container: [],
    };

    this.onResize = this.onResize.bind(this);
    this.viewMoreButtonClick = this.viewMoreButtonClick.bind(this);
    this.createCrossSell = this.createCrossSell.bind(this);
  }

  componentDidMount() {
    this.createCrossSell();

    window.addEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps) {
    const { products, title, productCode, id, displayProductTitles, displayProductPrices } = this.props;

    if (prevProps.productCode !== productCode || prevProps.products !== products) {
      this.createCrossSell();

      dlTrackCrossSellProducts(
        products.map(product =>
          getProductAdapter(product, {
            displayProductTitles,
            displayProductPrices,
          })
        ),
        title,
        id
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    const { productsPerRow } = this.state;
    const perRowForCurrentSize = CrossSell.productsPerRowForCurrentSize();
    if (perRowForCurrentSize !== productsPerRow) {
      this.setState({
        productsPerRow: perRowForCurrentSize,
        container: this.crossSellProductsContainer.resize(perRowForCurrentSize),
      });
    }
  }

  createCrossSell() {
    this.crossSellProductsContainer = this.createCrossSellProductsContainer();
    this.setState(currentState => ({
      container: this.crossSellProductsContainer.loadProducts(currentState.productsPerRow, currentState.productsPerRow),
    }));
  }

  createCrossSellProductsContainer() {
    const { products: allExistingProducts, displayProductTitles, displayProductPrices } = this.props;
    const metaInfo = { displayProductTitles, displayProductPrices };

    let nrOfDisplayedProducts = 0;

    const containerize = (products, productsPerRow) => {
      const makeEmptyContainer = () => {
        const nrOfRows = Math.ceil(products.length / productsPerRow);
        return Array.from(new Array(nrOfRows), () => ({
          products: [],
          rowRef: React.createRef(),
        }));
      };

      return products.reduce((container, product, index) => {
        container[Math.floor(index / productsPerRow)].products.push(getProductAdapter(product, metaInfo));
        return container;
      }, makeEmptyContainer());
    };

    let instance = [];

    return {
      leftToShow: () => allExistingProducts.length - nrOfDisplayedProducts,
      loadProducts: (numberOfNewProducts, productsPerRow) => {
        const products = allExistingProducts.slice(nrOfDisplayedProducts, nrOfDisplayedProducts + numberOfNewProducts);
        nrOfDisplayedProducts += products.length;
        const newRows = containerize(products, productsPerRow);
        instance = [...instance, ...newRows];
        return instance;
      },
      resize: productsPerRow => {
        nrOfDisplayedProducts = instance.length === 1 ? productsPerRow : nrOfDisplayedProducts;
        instance = containerize(allExistingProducts.slice(0, nrOfDisplayedProducts), productsPerRow);
        return instance;
      },
    };
  }

  viewMoreButtonClick(nrOfProducts) {
    const { productsPerRow } = this.state;
    this.setState({
      container: this.crossSellProductsContainer.loadProducts(nrOfProducts, productsPerRow),
    });
  }

  render() {
    const { container, productsPerRow } = this.state;
    const { title, productCode, id } = this.props;

    const crossSellTitle = title.replace(/-|_/g, ' ').toLowerCase();
    const productsLeftToShow = container.length > 0 ? this.crossSellProductsContainer.leftToShow() : 0;

    return (
      <div className="product-list__container cross-sell__container">
        <div className="product-carousel__title">
          <h2>{decodeHtml(title)}</h2>
        </div>
        <div className="product-list__container-grid">
          {container.map((row, rowIndex) => (
            <div
              className={`cross-sell__row js-tracking-${id}-crossSell-rows`}
              ref={row.rowRef}
              key={`csContainer_${productsPerRow}_${productCode}_${rowIndex + 1}`}
            >
              {row.products.map((product, itemIndex) => (
                <div className="cross-sell__item" key={`${product.id}_${itemIndex + 1}`} data-sku={product.id}>
                  <LazyLoad offset={200}>
                    <ProductItem
                      resultPosition={rowIndex * productsPerRow + itemIndex}
                      onClick={() => {
                        dlTrackProductClick({
                          product: Object.assign({}, product, {
                            crossSell: title,
                          }),
                          position: rowIndex * productsPerRow + itemIndex,
                          list: `product detail - ${title}`,
                        });
                        dlSelectCrossSellItem(product, rowIndex * productsPerRow + itemIndex);
                      }}
                      hasMoreProductsPerRow
                      key={product.id}
                      product={product}
                      appendQueryToUrl={`?crossSell=${encodeURIComponent(crossSellTitle)}`}
                      displayHeroBadge={false}
                    />
                  </LazyLoad>
                </div>
              ))}
            </div>
          ))}
        </div>
        {productsLeftToShow > 0 && (
          <div className="cross-sell__view-more-container">
            <button type="button" className="button" onClick={e => this.viewMoreButtonClick(productsLeftToShow, e)}>
              <FormattedMessage
                id="products.productgrid.actions.viewMore"
                description="View {x} more"
                values={{ 0: productsLeftToShow }}
              />
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default CrossSell;
